import React from 'react';

const SearchKeywordsForm = ({ data, errors, onUpdate }) => {
  const handleNestedChange = (parent, field, value) => {
    onUpdate(parent, {
      ...data[parent],
      [field]: value
    });
  };

  const handleArrayAdd = (parent, field) => {
    const currentArray = data[parent][field] || [];
    onUpdate(parent, {
      ...data[parent],
      [field]: [...currentArray, '']
    });
  };

  const handleArrayRemove = (parent, field, index) => {
    const currentArray = data[parent][field] || [];
    onUpdate(parent, {
      ...data[parent],
      [field]: currentArray.filter((_, i) => i !== index)
    });
  };

  const handleArrayChange = (parent, field, index, value) => {
    const currentArray = [...data[parent][field]];
    currentArray[index] = value;
    onUpdate(parent, {
      ...data[parent],
      [field]: currentArray
    });
  };

  return (
    <div className="form-section">
      <div className="section-header">
        <h3>
          Capabilities & Keywords
        </h3>
        <p>What you offer, so others can find you by capability rather than by name.</p>
      </div>

      {/* Capability Description - Full Width */}
      <div className="form-group full-width">
        <label htmlFor="capability_description" className="form-label">
          Capability Description
        </label>
        <textarea
          id="capability_description"
          className="form-textarea"
          rows="3"
          value={data.capabilities.capability_description || ''}
          onChange={(e) => handleNestedChange('capabilities', 'capability_description', e.target.value)}
          placeholder="Describe your company's capabilities, services, and digital twin offerings..."
        />
      </div>

      <div className="form-grid">
        {/* Language */}
        <div className="form-group">
          <label htmlFor="language" className="form-label">
            Primary Language
          </label>
          <select
            id="language"
            className="form-select"
            value={data.capabilities.language}
            onChange={(e) => handleNestedChange('capabilities', 'language', e.target.value)}
          >
            <option value="EN">English (EN)</option>
            <option value="DE">German (DE)</option>
            <option value="FR">French (FR)</option>
            <option value="ES">Spanish (ES)</option>
            <option value="IT">Italian (IT)</option>
            <option value="ZH">Chinese (ZH)</option>
            <option value="JA">Japanese (JA)</option>
          </select>
        </div>

        {/* Asset Kind */}
        <div className="form-group">
          <label htmlFor="asset_kind" className="form-label">
            Asset Kind
          </label>
          <select
            id="asset_kind"
            className="form-select"
            value={data.capabilities.asset_kind}
            onChange={(e) => handleNestedChange('capabilities', 'asset_kind', e.target.value)}
          >
            <option value="Instance">Instance</option>
            <option value="Type">Type</option>
          </select>
        </div>
      </div>

      {/* Keywords */}
      <div className="keywords-container">
        <div className="keyword-group keyword-group--keywords">
          <div className="keyword-header">
            <h4>
              Keywords
            </h4>
            <p>Search keywords for discoverability (e.g., AAS, registry, discovery, digital-twin)</p>
          </div>

          <div className="keyword-list">
            {data.capabilities.keywords && data.capabilities.keywords.map((keyword, index) => (
              <div key={index} className="keyword-item">
                <input
                  type="text"
                  className="form-input"
                  value={keyword}
                  onChange={(e) => handleArrayChange('capabilities', 'keywords', index, e.target.value)}
                  placeholder="e.g., AAS, registry, discovery"
                />
                <button
                  type="button"
                  className="btn-remove-keyword"
                  onClick={() => handleArrayRemove('capabilities', 'keywords', index)}
                  title="Remove keyword"
                >
                  Remove
                </button>
              </div>
            ))}

            {errors.keywords && (
              <div className="error-message">{errors.keywords}</div>
            )}

            <button
              type="button"
              className="btn-add-keyword"
              onClick={() => handleArrayAdd('capabilities', 'keywords')}
            >
              Add Keyword
            </button>
          </div>
        </div>

        {/* Capability Types */}
        <div className="keyword-group keyword-group--capabilities">
          <div className="keyword-header">
            <h4>
              Capability Types
            </h4>
            <p>Types of capabilities or services provided (e.g., AAS Registry & Discovery Profiles)</p>
          </div>

          <div className="keyword-list">
            {data.capabilities.capability_types && data.capabilities.capability_types.map((capType, index) => (
              <div key={index} className="keyword-item">
                <input
                  type="text"
                  className="form-input"
                  value={capType}
                  onChange={(e) => handleArrayChange('capabilities', 'capability_types', index, e.target.value)}
                  placeholder="e.g., AAS Registry & Discovery Profiles"
                />
                <button
                  type="button"
                  className="btn-remove-keyword"
                  onClick={() => handleArrayRemove('capabilities', 'capability_types', index)}
                  title="Remove capability type"
                >
                  Remove
                </button>
              </div>
            ))}

            {errors.capability_types && (
              <div className="error-message">{errors.capability_types}</div>
            )}

            <button
              type="button"
              className="btn-add-keyword"
              onClick={() => handleArrayAdd('capabilities', 'capability_types')}
            >
              Add Capability Type
            </button>
          </div>
        </div>

        {/* Submodels Supported */}
        <div className="keyword-group keyword-group--submodels">
          <div className="keyword-header">
            <h4>
              Submodels Supported
            </h4>
            <p>List of submodels your company supports (e.g., DigitalNameplate, Technical Data)</p>
          </div>

          <div className="keyword-list">
            {data.capabilities.submodels_supported && data.capabilities.submodels_supported.map((submodel, index) => (
              <div key={index} className="keyword-item">
                <input
                  type="text"
                  className="form-input"
                  value={submodel}
                  onChange={(e) => handleArrayChange('capabilities', 'submodels_supported', index, e.target.value)}
                  placeholder="e.g., DigitalNameplate, Technical Data"
                />
                <button
                  type="button"
                  className="btn-remove-keyword"
                  onClick={() => handleArrayRemove('capabilities', 'submodels_supported', index)}
                  title="Remove submodel"
                >
                  Remove
                </button>
              </div>
            ))}

            {errors.submodels_supported && (
              <div className="error-message">{errors.submodels_supported}</div>
            )}

            <button
              type="button"
              className="btn-add-keyword"
              onClick={() => handleArrayAdd('capabilities', 'submodels_supported')}
            >
              Add Submodel
            </button>
          </div>
        </div>
      </div>

      <div className="form-help">
        <h4>Examples:</h4>
        <div className="keyword-examples">
          <div className="keyword-example">
            <strong>Keywords:</strong>
            <div className="example-tags">
              <span className="tag">AAS</span>
              <span className="tag">registry</span>
              <span className="tag">discovery</span>
              <span className="tag">submodels</span>
              <span className="tag">digital-twin</span>
            </div>
          </div>
          <div className="keyword-example">
            <strong>Capability Types:</strong>
            <div className="example-tags">
              <span className="tag">AAS Registry & Discovery Profiles</span>
              <span className="tag">Submodel Template Catalog</span>
            </div>
          </div>
          <div className="keyword-example">
            <strong>Submodels Supported:</strong>
            <div className="example-tags">
              <span className="tag">DigitalNameplate</span>
              <span className="tag">Technical Data</span>
              <span className="tag">Handover Documentation</span>
              <span className="tag">Contact Information</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchKeywordsForm;
